import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Search, Stethoscope, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'cardiovascular', label: 'Cardiovascular', color: 'bg-red-100 text-red-800' },
  { value: 'respiratorio', label: 'Respiratorio', color: 'bg-blue-100 text-blue-800' },
  { value: 'neurologico', label: 'Neurológico', color: 'bg-purple-100 text-purple-800' },
  { value: 'trauma', label: 'Trauma', color: 'bg-orange-100 text-orange-800' },
  { value: 'infeccioso', label: 'Infeccioso', color: 'bg-green-100 text-green-800' },
  { value: 'metabolico', label: 'Metabólico', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'otro', label: 'Otro', color: 'bg-gray-100 text-gray-800' },
];

const URGENCY_COLORS = {
  alta: 'bg-red-500 text-white',
  media: 'bg-yellow-500 text-white',
  baja: 'bg-green-500 text-white'
};

const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[CATEGORIES.length - 1];

export default function DiagnosisModal({ 
  isOpen, 
  onClose, 
  sessionId, 
  onDiagnosisEmitted,
  emittedDiagnoses = []
}) {
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [diagnosisType, setDiagnosisType] = useState('principal');
  const [confidence, setConfidence] = useState('alta');
  const [justification, setJustification] = useState('');

  const fetchDiagnoses = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagnoses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnoses(response.data);
    } catch (error) {
      toast.error('Error al cargar diagnósticos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchDiagnoses();
      setSelectedDiagnosis(null);
      setJustification('');
      setDiagnosisType(emittedDiagnoses.some(d => d.tipo === 'principal') ? 'secundario' : 'principal');
    }
  }, [isOpen, fetchDiagnoses, emittedDiagnoses]);

  const filteredDiagnoses = diagnoses.filter(d => {
    const matchesSearch = d.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.codigo_cie10.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.nombre_corto && d.nombre_corto.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || d.categoria === selectedCategory;
    // Exclude already emitted diagnoses
    const notEmitted = !emittedDiagnoses.some(ed => ed.diagnosis_id === d.id);
    return matchesSearch && matchesCategory && notEmitted;
  });

  const handleSubmit = async () => {
    if (!selectedDiagnosis) {
      toast.error('Selecciona un diagnóstico');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/simulations/${sessionId}/emit-diagnosis`,
        {
          diagnosis_id: selectedDiagnosis.id,
          codigo_cie10: selectedDiagnosis.codigo_cie10,
          nombre: selectedDiagnosis.nombre_corto || selectedDiagnosis.nombre,
          tipo: diagnosisType,
          justificacion: justification || null,
          confianza: confidence
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Diagnóstico "${selectedDiagnosis.nombre_corto || selectedDiagnosis.nombre}" emitido`);
      
      if (onDiagnosisEmitted) {
        onDiagnosisEmitted({
          ...response.data,
          diagnosis_id: selectedDiagnosis.id,
          codigo_cie10: selectedDiagnosis.codigo_cie10,
          nombre: selectedDiagnosis.nombre_corto || selectedDiagnosis.nombre,
          tipo: diagnosisType
        });
      }
      
      onClose();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al emitir diagnóstico';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-blue-600" />
            Emitir Diagnóstico Clínico
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o código CIE-10..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-diagnosis"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Diagnoses List */}
          <ScrollArea className="h-[250px] border rounded-md p-2">
            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                Cargando diagnósticos...
              </div>
            ) : filteredDiagnoses.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                No se encontraron diagnósticos
              </div>
            ) : (
              <div className="space-y-2">
                {filteredDiagnoses.map(diagnosis => {
                  const catInfo = getCategoryInfo(diagnosis.categoria);
                  const isSelected = selectedDiagnosis?.id === diagnosis.id;
                  return (
                    <div
                      key={diagnosis.id}
                      onClick={() => setSelectedDiagnosis(diagnosis)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid={`diagnosis-option-${diagnosis.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-xs">
                              {diagnosis.codigo_cie10}
                            </Badge>
                            <span className="font-medium">
                              {diagnosis.nombre_corto || diagnosis.nombre}
                            </span>
                            {isSelected && <CheckCircle className="h-4 w-4 text-blue-600" />}
                          </div>
                          {diagnosis.nombre_corto && (
                            <p className="text-xs text-gray-500 mt-1">{diagnosis.nombre}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={catInfo.color} variant="secondary">
                            {catInfo.label}
                          </Badge>
                          <Badge className={URGENCY_COLORS[diagnosis.urgencia]}>
                            {diagnosis.urgencia}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Selected Diagnosis Details */}
          {selectedDiagnosis && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600" />
                <span className="font-medium">Diagnóstico seleccionado:</span>
                <Badge variant="outline" className="font-mono">{selectedDiagnosis.codigo_cie10}</Badge>
                <span>{selectedDiagnosis.nombre_corto || selectedDiagnosis.nombre}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Diagnóstico</Label>
                  <Select value={diagnosisType} onValueChange={setDiagnosisType}>
                    <SelectTrigger data-testid="diagnosis-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="principal">Principal</SelectItem>
                      <SelectItem value="secundario">Secundario</SelectItem>
                      <SelectItem value="diferencial">Diferencial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nivel de Confianza</Label>
                  <Select value={confidence} onValueChange={setConfidence}>
                    <SelectTrigger data-testid="diagnosis-confidence">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Justificación (opcional)</Label>
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Explica brevemente por qué llegas a este diagnóstico..."
                  rows={2}
                  data-testid="diagnosis-justification"
                />
              </div>

              {selectedDiagnosis.criterios_diagnosticos?.length > 0 && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Criterios diagnósticos:</span>
                  <ul className="list-disc list-inside text-gray-600 mt-1">
                    {selectedDiagnosis.criterios_diagnosticos.slice(0, 3).map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedDiagnosis || submitting}
            data-testid="submit-diagnosis"
          >
            {submitting ? 'Emitiendo...' : 'Emitir Diagnóstico'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
